import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import _ from "lodash";

import { getPeriodOverPeriodMaxAllowedDuration } from "app/components/duration-selector/period-over-period/periodOverPeriodMaxAllowedDuration";
import { swSettings } from "app/common/services/swSettings";
import widgetSettings from "app/components/dashboard/WidgetSettings";
import { translate } from "app/filters/i18nFilter";

import type { IWidgetModelTypesFamily } from "../components/widget/widget-types/Widget";

import type { IDurationData } from "./DurationService";
import DurationService from "./DurationService";
import type { IPeriodOverPeriodCompareType } from "./periodOverPeriodTypes";

export class PeriodOverPeriodService {
    private MAX_MONTHS_DIFF = 12;
    private DEFAULT_COMPARED_DURATION = "12m";
    public static COMPARE_TYPES = {
        YEAR: "previous year" as IPeriodOverPeriodCompareType,
        PERIOD: "previous period" as IPeriodOverPeriodCompareType,
    };
    private periodOverPeriodDefaultVs: string;

    getPeriodOverPeriodMaxDuration() {
        const periodOverPeriodMaxAllowedDuration = parseInt(getPeriodOverPeriodMaxAllowedDuration());

        if (periodOverPeriodMaxAllowedDuration) {
            return Math.max(this.MAX_MONTHS_DIFF, periodOverPeriodMaxAllowedDuration);
        } else {
            return this.MAX_MONTHS_DIFF;
        }
    }

    periodOverPeriodEnabledForMetric(metricId: string, isCompare: boolean, family?: IWidgetModelTypesFamily) {
        if (["Website", "Industry", "Segments", "Mobile"].indexOf(family) === -1) {
            return false;
        }
        const metricWidgets = widgetSettings.getMetricWidgets(metricId, isCompare);
        return !_.isUndefined(_.find(metricWidgets, "properties.periodOverPeriodSupport"));
    }
    /**
     * Whether or not to enable the period-over-period dropdown
     * @param durationParam
     * @param comparedDurationParam
     * @param key
     * @param componentId
     * @returns {boolean}
     */
    _periodOverPeriodEnabled(
        durationParam: string,
        comparedDurationParam: string,
        key: string | unknown[],
        componentId: string,
    ): boolean {
        // do not allow period over period in 28d or in more then 1 year
        comparedDurationParam = comparedDurationParam || this.DEFAULT_COMPARED_DURATION;
        if (!durationParam) {
            return false;
        }

        const duration: IDurationData = DurationService.getDurationData(
            durationParam,
            comparedDurationParam,
            componentId,
            false,
        );

        const durationMonths = DurationService.diffByUnit(duration.forAPI.from, duration.forAPI.to, "months") + 1;
        const periodOverPeriodMaxAllowedDuration = parseInt(getPeriodOverPeriodMaxAllowedDuration());

        if (periodOverPeriodMaxAllowedDuration && periodOverPeriodMaxAllowedDuration < durationMonths) {
            return false;
        }
        const keyArray = _.isArray(key) ? key : key?.split(",");
        const comparePeriodFrom: Dayjs = duration.raw.compareFrom;
        const poPstartDate = swSettings.components[componentId].resources.PeriodOverPeriodStartDate;
        // don't allow period over period
        // 1. moving window === 28d
        // 2. more then 12 months or before minimum start date
        // 3. compare mode
        // 4. the user don't have permission to see period over period
        const isKeyArrayValid =
            swSettings.components[componentId].resources.AllowPeriodOverPeriodWithCompare || keyArray?.length <= 1;
        if (
            durationParam == "28d" ||
            !this._isValidDuration(duration, comparePeriodFrom, componentId) ||
            !isKeyArrayValid ||
            !duration.forAPI.compareFrom ||
            !duration.forAPI.compareTo ||
            (poPstartDate && comparePeriodFrom.isBefore(poPstartDate))
        ) {
            return false;
        } else {
            return true;
        }
    }

    periodOverPeriodEnabled(
        durationParam: string,
        comparedDurationParam: string,
        key: string | unknown[],
        componentId: string,
        periodOverPeriodSymbol?: string,
    ): boolean {
        const _popItems = this.getPeriodOverPeriodDropdownItems(
            durationParam,
            key,
            componentId,
            false,
            periodOverPeriodSymbol,
        );
        const _hasEnabled =
            comparedDurationParam === "12m"
                ? _.find(_popItems, {
                      disabled: false,
                      id: PeriodOverPeriodService.COMPARE_TYPES.YEAR,
                  })
                : _.find(_popItems, { disabled: false });
        return !_.isUndefined(_hasEnabled);
    }
    /**
     * Get the available values for the period-over-period dropown
     * @returns {[{text: any, id: string},{text: any, id: string}]}
     */
    getPeriodOverPeriodDropdownItems(
        duration,
        keys,
        componentId,
        overrideEnabled = false,
        periodOverPeriodSymbol?: string,
    ) {
        const previousYear = {
            text: translate("compared.duration.dropdown.previous.year"),
            id: PeriodOverPeriodService.COMPARE_TYPES.YEAR,
            disabled: overrideEnabled ? false : !this._periodOverPeriodEnabled(duration, "12m", keys, componentId),
        };
        const previousPeriod = {
            text: translate("compared.duration.dropdown.previous.period"),
            id: PeriodOverPeriodService.COMPARE_TYPES.PERIOD,
            disabled: overrideEnabled
                ? false
                : !this._periodOverPeriodEnabled(duration, periodOverPeriodSymbol || duration, keys, componentId),
        };
        return [previousYear, previousPeriod];
    }

    /**
     * Returns the type of the comparison, for showing the correct value in the dropdown
     * @param duration
     * @returns {string|string}
     */
    getPeriodOverPeriodType(duration: string) {
        return duration == "12m"
            ? PeriodOverPeriodService.COMPARE_TYPES.YEAR
            : PeriodOverPeriodService.COMPARE_TYPES.PERIOD;
    }

    /**
     * This function calculates the compared duration. Currently we support only compared duration which is the same length as the main duration
     * @param duration
     * @returns {string}
     */
    calculateComparedDuration(compareDuration) {
        const durationObject = DurationService.getDurationData(compareDuration);
        // @ts-ignore FIXME: getDiffSymbol expects Dayjs but forAPI.from/to are strings — callers should use raw (Dayjs) dates instead
        return DurationService.getDiffSymbol(durationObject.forAPI.from, durationObject.forAPI.to, "months");
    }

    /**
     * Calculates if the duration valid for period over period.
     * @param {String} duration - selected period
     * @param {Moment} compareDurationFrom - start date of compare period
     * @param {String} componentId - component name
     * @returns {boolean}
     * @privates
     */
    private _isValidDuration(duration, compareDurationFrom: Dayjs, componentId: string): boolean {
        const minimumStartDate = dayjs.utc(
            swSettings.components[componentId].resources.PeriodOverPeriodStartDate,
            "YYYY-MM",
        );
        const monthsDiff = DurationService.getMonthsFromApiDuration(duration.raw.from, duration.raw.to, false);

        // if the range is more then 12 months or the start date is prior to the minimum start date
        if (duration.raw.from.isBefore(minimumStartDate) || monthsDiff > this.getPeriodOverPeriodMaxDuration()) {
            return false;
        }
        // if from duration is less that minimum possible
        if (
            compareDurationFrom &&
            compareDurationFrom.isBefore(minimumStartDate) &&
            !duration.raw.from.isSame(compareDurationFrom)
        ) {
            return false;
        }
        // the compared duration cannot be before the user's minimum start date
        if (
            duration.raw.compareFrom &&
            duration.raw.compareFrom.isBefore(swSettings.components[componentId].startDate)
        ) {
            return false;
        }
        return true;
    }
}

export const periodOverPeriodService = new PeriodOverPeriodService();
